import React, { useState } from 'react';
import { ArrowLeft, Printer, Mail, Download, Shield, CheckCircle2, AlertTriangle, Activity, Server, Cloud, Database, Lock, Users, Triangle, X, Plus, Paperclip, Send, Check, Loader2 } from 'lucide-react';
import type { Environment } from '../components/RecentScans';
import type { BrandingConfig } from '../App';
interface ReportPageProps {
  environment: Environment;
  selectedSections: string[];
  onBack: () => void;
  branding?: BrandingConfig;
}
export function ReportPage({
  environment,
  selectedSections,
  onBack,
  branding
}: ReportPageProps) {
  const brandName = branding?.brandName || 'Astrolayb';
  const brandColor = branding?.primaryColor || '#22c55e';
  const logoUrl = branding?.logoUrl || '';
  const footerText = branding?.reportFooter || 'Confidential — Generated by Astrolayb Security Intelligence Platform';
  const showPoweredBy = !branding?.removePoweredBy;
  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [toEmails, setToEmails] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const handlePrint = () => {
    window.print();
  };
  const handleEmail = () => {
    setEmailSubject(`Security Remediation Report: ${environment.clientName}`);
    setEmailBody(`Hi,\n\nPlease find attached the security remediation report for ${environment.clientName}.\n\nThis report covers ${selectedSections.length} sections including ${selectedSections.map(s => s.replace(/-/g, ' ')).join(', ')}.\n\nGenerated on ${new Date().toLocaleDateString()}\n\nBest regards`);
    setToEmails('');
    setCcEmails('');
    setShowCc(false);
    setEmailSending(false);
    setEmailSent(false);
    setShowEmailDialog(true);
  };
  const handleSendEmail = () => {
    if (!toEmails.trim()) return;
    setEmailSending(true);
    setTimeout(() => {
      setEmailSending(false);
      setEmailSent(true);
      setTimeout(() => {
        setShowEmailDialog(false);
        setEmailSent(false);
      }, 1800);
    }, 1500);
  };
  const getTypeIcon = (type: Environment['type']) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={16} data-id="element-1649" />;
      case 'On-Prem':
        return <Server size={16} data-id="element-1650" />;
      case 'Hybrid':
        return <Database size={16} data-id="element-1651" />;
      default:
        return <Server size={16} data-id="element-1652" />;
    }
  };
  return <div className="min-h-screen bg-[#0a0e14] pb-12" data-id="element-1653">
      {/* Toolbar - Hidden on Print */}
      <div className="sticky top-0 z-10 bg-[#0a0e14]/95 backdrop-blur border-b border-astro-border p-4 print:hidden" data-id="element-1654">
        <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-1655">
          <div className="flex items-center gap-4" data-id="element-1656">
            <button onClick={onBack} className="flex items-center gap-2 text-sm text-astro-text-secondary hover:text-white transition-colors" data-id="element-1657">
              <ArrowLeft size={16} data-id="element-1658" />
              Back
            </button>
            <div className="h-6 w-px bg-astro-border" data-id="element-1659"></div>
            <h1 className="text-lg font-bold text-white" data-id="element-1660">
              Report Preview: {environment.clientName}
            </h1>
          </div>
          <div className="flex items-center gap-3" data-id="element-1661">
            <button onClick={handlePrint} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/20 transition-colors" data-id="element-1662">
              <Printer size={16} data-id="element-1663" />
              Print
            </button>
            <button onClick={handleEmail} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/20 transition-colors" data-id="element-1664">
              <Mail size={16} data-id="element-1665" />
              Email
            </button>
            <button onClick={handlePrint} className="flex items-center gap-2 px-3 py-1.5 text-sm font-bold text-black bg-astro-green hover:bg-astro-green/90 rounded transition-colors" data-id="element-1666">
              <Download size={16} data-id="element-1667" />
              Download PDF
            </button>
          </div>
        </div>
      </div>

      {/* Report Document */}
      <div className="max-w-4xl mx-auto mt-8 bg-white text-slate-900 shadow-2xl print:shadow-none print:mt-0 min-h-[1100px]" data-id="element-1668">
        {/* Header / Cover */}
        <div className="p-12 border-b border-slate-200" data-id="element-1669">
          <div className="flex justify-between items-start mb-12" data-id="element-1670">
            <div data-id="element-1671">
              <div className="flex items-center gap-2 mb-1" style={{
              color: brandColor
            }} data-id="element-1672">
                {logoUrl ? <img src={logoUrl} alt={brandName} className="h-8 max-w-[200px] object-contain" data-id="element-1673" /> : <>
                    <Triangle size={24} style={{
                  fill: brandColor,
                  color: brandColor
                }} strokeWidth={2.5} data-id="element-1674" />

                    <span className="text-xl font-bold tracking-wider uppercase" data-id="element-1675">
                      {brandName}
                    </span>
                  </>}
              </div>
              <p className="text-sm text-slate-500 font-medium" data-id="element-1676">
                Security Intelligence Platform
              </p>
            </div>
            <div className="text-right" data-id="element-1677">
              <div className="text-sm text-slate-500 mb-1" data-id="element-1678">
                Report Generated
              </div>
              <div className="font-mono font-medium" data-id="element-1679">
                {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-slate-900 mb-6" data-id="element-1680">
            Security Remediation Report
          </h1>

          <div className="flex items-center gap-4 mb-8" data-id="element-1681">
            <div className="px-4 py-2 bg-slate-100 rounded-lg border border-slate-200" data-id="element-1682">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1" data-id="element-1683">
                Client
              </span>
              <span className="text-lg font-bold text-slate-900" data-id="element-1684">
                {environment.clientName}
              </span>
            </div>
            <div className="px-4 py-2 bg-slate-100 rounded-lg border border-slate-200" data-id="element-1685">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1" data-id="element-1686">
                Environment
              </span>
              <div className="flex items-center gap-2" data-id="element-1687">
                <span className="text-slate-600" data-id="element-1688">
                  {getTypeIcon(environment.type)}
                </span>
                <span className="text-lg font-bold text-slate-900" data-id="element-1689">
                  {environment.type}
                </span>
              </div>
            </div>
            <div className="px-4 py-2 bg-slate-100 rounded-lg border border-slate-200" data-id="element-1690">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1" data-id="element-1691">
                Compliance Score
              </span>
              <span className={`text-lg font-mono font-bold ${environment.complianceScore > 85 ? 'text-green-600' : environment.complianceScore > 70 ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-1692">
                {environment.complianceScore}%
              </span>
            </div>
          </div>

          {(environment.govCloud || environment.cmmcLevel) && <div className="flex gap-2" data-id="element-1693">
              {environment.govCloud && <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-xs font-bold uppercase tracking-wider" data-id="element-1694">
                  Government Cloud
                </span>}
              {environment.cmmcLevel && <span className="px-2 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded text-xs font-bold uppercase tracking-wider" data-id="element-1695">
                  CMMC Level {environment.cmmcLevel}
                </span>}
              {environment.complianceFrameworks?.map(fw => <span key={fw} className="px-2 py-1 bg-slate-100 text-slate-600 border border-slate-200 rounded text-xs font-medium" data-id="element-1696">
                  {fw}
                </span>)}
            </div>}
        </div>

        {/* Executive Summary */}
        {selectedSections.includes('executive-summary') && <div className="p-12 border-b border-slate-200 break-inside-avoid" data-id="element-1697">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1698">
              <Activity className="text-slate-400" data-id="element-1699" />
              Executive Summary
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8 text-lg" data-id="element-1700">
              This report outlines critical security findings for{' '}
              {environment.clientName}'s {environment.type} environment.
              Analysis detected{' '}
              <span className="font-bold text-slate-900" data-id="element-1701">
                {environment.overPrivileged} over-privileged identities
              </span>{' '}
              and potential compliance gaps. The overall security posture is
              currently rated as
              <span className={`font-bold mx-1 ${environment.status === 'critical' ? 'text-red-600' : environment.status === 'warning' ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-1702">
                {environment.status.toUpperCase()}
              </span>
              . Immediate remediation of high-severity findings is recommended
              to reduce the attack surface and ensure regulatory compliance.
            </p>

            <div className="grid grid-cols-3 gap-6" data-id="element-1703">
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200" data-id="element-1704">
                <div className="text-sm text-slate-500 font-medium mb-2" data-id="element-1705">
                  Total Identities
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-1706">
                  {environment.identities.toLocaleString()}
                </div>
              </div>
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200" data-id="element-1707">
                <div className="text-sm text-slate-500 font-medium mb-2" data-id="element-1708">
                  Critical Risks
                </div>
                <div className="text-3xl font-bold text-red-600" data-id="element-1709">
                  {environment.overPrivileged}
                </div>
              </div>
              <div className="p-6 bg-slate-50 rounded-xl border border-slate-200" data-id="element-1710">
                <div className="text-sm text-slate-500 font-medium mb-2" data-id="element-1711">
                  Security Score
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-1712">
                  {environment.complianceScore}/100
                </div>
              </div>
            </div>
          </div>}

        {/* Over-Privileged Identities */}
        {selectedSections.includes('over-privileged') && <div className="p-12 border-b border-slate-200 break-inside-avoid" data-id="element-1713">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1714">
              <AlertTriangle className="text-slate-400" data-id="element-1715" />
              Over-Privileged Identities
            </h2>

            <div className="mb-8" data-id="element-1716">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-1717">
                Risk Distribution
              </h3>
              <div className="h-4 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-1718">
                <div className="h-full bg-red-500 w-[15%]" data-id="element-1719"></div>
                <div className="h-full bg-orange-400 w-[25%]" data-id="element-1720"></div>
                <div className="h-full bg-yellow-400 w-[30%]" data-id="element-1721"></div>
                <div className="h-full bg-slate-300 w-[30%]" data-id="element-1722"></div>
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-500 font-medium" data-id="element-1723">
                <div className="flex items-center gap-1" data-id="element-1724">
                  <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-1725"></div>
                  Critical (15%)
                </div>
                <div className="flex items-center gap-1" data-id="element-1726">
                  <div className="w-2 h-2 rounded-full bg-orange-400" data-id="element-1727"></div>High
                  (25%)
                </div>
                <div className="flex items-center gap-1" data-id="element-1728">
                  <div className="w-2 h-2 rounded-full bg-yellow-400" data-id="element-1729"></div>
                  Medium (30%)
                </div>
                <div className="flex items-center gap-1" data-id="element-1730">
                  <div className="w-2 h-2 rounded-full bg-slate-300" data-id="element-1731"></div>Low
                  (30%)
                </div>
              </div>
            </div>

            <table className="w-full text-left text-sm" data-id="element-1732">
              <thead data-id="element-1733">
                <tr className="border-b border-slate-200" data-id="element-1734">
                  <th className="pb-3 font-bold text-slate-500 uppercase text-xs" data-id="element-1735">
                    Identity
                  </th>
                  <th className="pb-3 font-bold text-slate-500 uppercase text-xs" data-id="element-1736">
                    Type
                  </th>
                  <th className="pb-3 font-bold text-slate-500 uppercase text-xs" data-id="element-1737">
                    Risk Level
                  </th>
                  <th className="pb-3 font-bold text-slate-500 uppercase text-xs text-right" data-id="element-1738">
                    Permissions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1739">
                {[1, 2, 3].map(i => <tr key={i} data-id="element-1740">
                    <td className="py-4 font-mono text-slate-700" data-id="element-1741">
                      svc-deploy-prod-{i}
                    </td>
                    <td className="py-4 text-slate-600" data-id="element-1742">Service Account</td>
                    <td className="py-4" data-id="element-1743">
                      <span className="px-2 py-1 bg-red-50 text-red-700 border border-red-100 rounded text-xs font-bold uppercase" data-id="element-1744">
                        Critical
                      </span>
                    </td>
                    <td className="py-4 text-right text-slate-600" data-id="element-1745">
                      Owner, Admin
                    </td>
                  </tr>)}
                {[1, 2].map(i => <tr key={`m-${i}`} data-id="element-1746">
                    <td className="py-4 font-mono text-slate-700" data-id="element-1747">
                      admin-user-{i}@client.com
                    </td>
                    <td className="py-4 text-slate-600" data-id="element-1748">User</td>
                    <td className="py-4" data-id="element-1749">
                      <span className="px-2 py-1 bg-orange-50 text-orange-700 border border-orange-100 rounded text-xs font-bold uppercase" data-id="element-1750">
                        High
                      </span>
                    </td>
                    <td className="py-4 text-right text-slate-600" data-id="element-1751">
                      Global Admin
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}

        {/* Compliance Posture */}
        {selectedSections.includes('compliance') && <div className="p-12 border-b border-slate-200 break-inside-avoid" data-id="element-1752">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1753">
              <CheckCircle2 className="text-slate-400" data-id="element-1754" />
              Compliance Posture
            </h2>

            <div className="flex items-center gap-8 mb-8" data-id="element-1755">
              <div className="flex-1" data-id="element-1756">
                <div className="flex justify-between mb-2" data-id="element-1757">
                  <span className="font-bold text-slate-700" data-id="element-1758">
                    Current Score
                  </span>
                  <span className="font-bold text-slate-900" data-id="element-1759">
                    {environment.complianceScore}%
                  </span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-1760">
                  <div className={`h-full rounded-full ${environment.complianceScore > 85 ? 'bg-green-500' : 'bg-yellow-500'}`} style={{
                width: `${environment.complianceScore}%`
              }} data-id="element-1761"></div>
                </div>
              </div>
              <div className="flex-1" data-id="element-1762">
                <div className="flex justify-between mb-2" data-id="element-1763">
                  <span className="font-bold text-slate-700" data-id="element-1764">Target Score</span>
                  <span className="font-bold text-slate-900" data-id="element-1765">90%</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-1766">
                  <div className="h-full bg-slate-300 rounded-full w-[90%]" data-id="element-1767"></div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl border border-slate-200 p-6" data-id="element-1768">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-1769">
                Framework Adherence
              </h3>
              <div className="space-y-4" data-id="element-1770">
                <div className="flex items-center justify-between" data-id="element-1771">
                  <span className="font-medium text-slate-700" data-id="element-1772">
                    NIST 800-171
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-bold" data-id="element-1773">
                    COMPLIANT
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1774">
                  <span className="font-medium text-slate-700" data-id="element-1775">
                    SOC 2 Type II
                  </span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-bold" data-id="element-1776">
                    IN REVIEW
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-1777">
                  <span className="font-medium text-slate-700" data-id="element-1778">ISO 27001</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-bold" data-id="element-1779">
                    COMPLIANT
                  </span>
                </div>
                {environment.govCloud && <div className="flex items-center justify-between" data-id="element-1780">
                    <span className="font-medium text-slate-700" data-id="element-1781">
                      FedRAMP High
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold" data-id="element-1782">
                      GAP DETECTED
                    </span>
                  </div>}
              </div>
            </div>
          </div>}

        {/* Remediation Steps */}
        {selectedSections.includes('recommendations') && <div className="p-12 border-b border-slate-200 break-inside-avoid" data-id="element-1783">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1784">
              <Shield className="text-slate-400" data-id="element-1785" />
              Recommended Actions
            </h2>

            <div className="space-y-6" data-id="element-1786">
              {[{
            title: 'Enforce MFA for all Admin Accounts',
            priority: 'Critical',
            impact: 'Prevents 99.9% of identity-based attacks',
            effort: 'Low'
          }, {
            title: 'Remove Unused Service Principals',
            priority: 'High',
            impact: 'Reduces attack surface by 15%',
            effort: 'Medium'
          }, {
            title: 'Implement Least-Privilege for S3 Buckets',
            priority: 'High',
            impact: 'Prevents data exfiltration',
            effort: 'High'
          }, {
            title: 'Rotate Access Keys older than 90 days',
            priority: 'Medium',
            impact: 'Ensures credential hygiene',
            effort: 'Low'
          }].map((action, i) => <div key={i} className="flex gap-4" data-id="element-1787">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-slate-500" data-id="element-1788">
                    {i + 1}
                  </div>
                  <div className="flex-1" data-id="element-1789">
                    <div className="flex items-center justify-between mb-1" data-id="element-1790">
                      <h3 className="font-bold text-slate-900" data-id="element-1791">
                        {action.title}
                      </h3>
                      <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${action.priority === 'Critical' ? 'bg-red-100 text-red-800' : action.priority === 'High' ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1792">
                        {action.priority}
                      </span>
                    </div>
                    <div className="flex gap-6 text-sm text-slate-500" data-id="element-1793">
                      <span data-id="element-1794">
                        Impact:{' '}
                        <span className="text-slate-700" data-id="element-1795">{action.impact}</span>
                      </span>
                      <span data-id="element-1796">
                        Effort:{' '}
                        <span className="text-slate-700" data-id="element-1797">{action.effort}</span>
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}

        {/* Footer */}
        <div className="p-12 bg-slate-50 text-center text-slate-500 text-sm" data-id="element-1798">
          <p className="mb-2" data-id="element-1799">{footerText}</p>
          {showPoweredBy && brandName !== 'Astrolayb' && <p className="text-xs text-slate-400" data-id="element-1800">Powered by Astrolayb</p>}
          {brandName === 'Astrolayb' && <p data-id="element-1801">Confidential - For Internal Use Only</p>}
        </div>
      </div>

      {/* Email Dialog */}
      {showEmailDialog && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 print:hidden" data-id="element-1802">
          <div className="w-full max-w-[560px] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-1803">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-astro-border" data-id="element-1804">
              <div className="flex items-center gap-3" data-id="element-1805">
                <div className="w-8 h-8 rounded-lg bg-astro-green/10 flex items-center justify-center" data-id="element-1806">
                  <Mail size={16} className="text-astro-green" data-id="element-1807" />
                </div>
                <div data-id="element-1808">
                  <h2 className="text-lg font-bold text-white" data-id="element-1809">Email Report</h2>
                  <p className="text-xs text-astro-text-secondary" data-id="element-1810">
                    Send this report to recipients
                  </p>
                </div>
              </div>
              <button onClick={() => setShowEmailDialog(false)} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-1811">
                <X size={20} data-id="element-1812" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4" data-id="element-1813">
              {/* To Field */}
              <div data-id="element-1814">
                <div className="flex items-center justify-between mb-1.5" data-id="element-1815">
                  <label className="text-sm font-medium text-astro-text-secondary" data-id="element-1816">
                    To
                  </label>
                  {!showCc && <button onClick={() => setShowCc(true)} className="text-[11px] text-astro-green hover:text-astro-green-dark transition-colors font-medium" data-id="element-1817">
                      + Add CC
                    </button>}
                </div>
                <input type="text" value={toEmails} onChange={e => setToEmails(e.target.value)} placeholder="recipient@company.com, another@company.com" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2.5 text-sm text-white placeholder:text-astro-text-secondary/40 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1818" />
              </div>

              {/* CC Field */}
              {showCc && <div data-id="element-1819">
                  <label className="text-sm font-medium text-astro-text-secondary block mb-1.5" data-id="element-1820">
                    CC
                  </label>
                  <input type="text" value={ccEmails} onChange={e => setCcEmails(e.target.value)} placeholder="cc@company.com" className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2.5 text-sm text-white placeholder:text-astro-text-secondary/40 focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1821" />
                </div>}

              {/* Subject */}
              <div data-id="element-1822">
                <label className="text-sm font-medium text-astro-text-secondary block mb-1.5" data-id="element-1823">
                  Subject
                </label>
                <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2.5 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-1824" />
              </div>

              {/* Message Body */}
              <div data-id="element-1825">
                <label className="text-sm font-medium text-astro-text-secondary block mb-1.5" data-id="element-1826">
                  Message
                </label>
                <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={5} className="w-full bg-[#0a0e14] border border-astro-border rounded px-3 py-2.5 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors resize-none leading-relaxed" data-id="element-1827" />
              </div>

              {/* Attachment Indicator */}
              <div className="flex items-center gap-3 p-3 bg-[#0a0e14] border border-astro-border rounded-lg" data-id="element-1828">
                <div className="w-8 h-8 rounded bg-astro-green/10 flex items-center justify-center shrink-0" data-id="element-1829">
                  <Paperclip size={14} className="text-astro-green" data-id="element-1830" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-1831">
                  <div className="text-sm font-medium text-white truncate" data-id="element-1832">
                    {environment.clientName} — Security Remediation Report.pdf
                  </div>
                  <div className="text-[11px] text-astro-text-secondary" data-id="element-1833">
                    {selectedSections.length} sections · Generated{' '}
                    {new Date().toLocaleDateString()}
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-astro-green/10 text-astro-green border border-astro-green/20 shrink-0" data-id="element-1834">
                  Attached
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-5 border-t border-astro-border" data-id="element-1835">
              <p className="text-[11px] text-astro-text-secondary" data-id="element-1836">
                Report will be sent as a PDF attachment
              </p>
              <div className="flex items-center gap-3" data-id="element-1837">
                <button onClick={() => setShowEmailDialog(false)} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-1838">
                  Cancel
                </button>
                {emailSent ? <div className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-astro-green bg-astro-green/10 border border-astro-green/20 rounded" data-id="element-1839">
                    <Check size={16} data-id="element-1840" />
                    Sent!
                  </div> : <button onClick={handleSendEmail} disabled={!toEmails.trim() || emailSending} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-black bg-astro-green hover:bg-astro-green/90 rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.2)] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1841">
                    {emailSending ? <>
                        <Loader2 size={16} className="animate-spin" data-id="element-1842" />
                        Sending…
                      </> : <>
                        <Send size={16} data-id="element-1843" />
                        Send Email
                      </>}
                  </button>}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}